/**
 * Finances — formatting and calendar arithmetic, ported from the mockup's helpers (Dan's spec, "Formatting"):
 * - Money: symbol, grouped digits, two decimals (JPY and INR in whole units); a true minus sign (U+2212);
 *   the compact form has one decimal ($3.6k, $57.1k, $1.2M).
 * - Dates: calendar days compared as UTC days, never a local date through `toISOString` (that put Today one day
 *   off in UTC+ zones); short form "26 May", the year added on request.
 */

const SYM: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', BRL: 'R$', JPY: '¥', INR: '₹', CAD: 'CA$', AUD: 'A$', CHF: 'CHF ', MXN: 'MX$',
};
/** Currencies without minor units on paper. */
const NODEC: Record<string, boolean> = { JPY: true, INR: true };
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** The currency's name in sentences ("Shown in euros"). */
export const CCY_NAME: Record<string, string> = {
  USD: 'US dollars', EUR: 'Euros', GBP: 'British pounds', BRL: 'Brazilian reais', JPY: 'Japanese yen', INR: 'Indian rupees',
};

/** Rounds to cents. */
export function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Rounds to the currency's smallest unit: cents, or whole yen and rupees. */
export function roundCcy(n: number, ccy: string): number {
  return NODEC[ccy] ? Math.round(n) : r2(n);
}

export function sum<T>(arr: readonly T[], f: (x: T) => number): number {
  let s = 0;
  for (const x of arr) s += f(x);
  return s;
}

export function currencySymbol(ccy: string): string {
  return SYM[ccy] ?? ccy + ' ';
}

function group(numStr: string): string {
  const [int, dec] = numStr.split('.');
  const g = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return dec !== undefined ? g + '.' + dec : g;
}

function trim1(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '');
}

export interface MoneyOptions {
  /** One decimal with k / M: $3.6k. */
  compact?: boolean;
  /** `false` drops the decimals ($1,840). */
  cents?: boolean;
}

/** "$3,573.16", "€863.30", "¥89,200", "−$5,891.01", compact "$57.1k". */
export function fmtMoney(amount: number, ccy = 'USD', o: MoneyOptions = {}): string {
  const sym = currencySymbol(ccy);
  const neg = amount < 0;
  const a = Math.abs(amount);
  let s: string;
  if (o.compact) {
    if (a >= 1e6) s = trim1(a / 1e6) + 'M';
    else if (a >= 1e3) s = trim1(a / 1e3) + 'k';
    else s = String(Math.round(a));
  } else {
    const dec = NODEC[ccy] ? 0 : o.cents === false ? 0 : 2;
    s = group(a.toFixed(dec));
  }
  return (neg ? '−' : '') + sym + s;
}

// ── Calendar days (ISO strings, UTC arithmetic) ──────────────────────────────────────────────────

function pad(n: number): string {
  return (n < 10 ? '0' : '') + n;
}

export function toUTC(iso: string): number {
  const p = iso.slice(0, 10).split('-');
  return Date.UTC(+p[0], +p[1] - 1, +p[2]);
}

export function fromUTC(ms: number): string {
  const d = new Date(ms);
  return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
}

export function addDays(iso: string, n: number): string {
  return fromUTC(toUTC(iso) + n * 86400000);
}

/** Same day n months later, clamped to the month's last day (31 Jan + 1 month = 28 Feb). */
export function addMonths(iso: string, n: number): string {
  const p = iso.split('-');
  let y = +p[0];
  let m = +p[1] - 1 + n;
  const d = +p[2];
  y += Math.floor(m / 12);
  m = ((m % 12) + 12) % 12;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return y + '-' + pad(m + 1) + '-' + pad(Math.min(d, last));
}

/** Whole days from a to b (negative when b is earlier). */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUTC(b) - toUTC(a)) / 86400000);
}

/** "2026-05". */
export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

/** "May" for "2026-05". */
export function monthLabel(key: string): string {
  return MONTHS[+key.slice(5, 7) - 1];
}

export function monthLong(key: string): string {
  return MONTHS_LONG[+key.slice(5, 7) - 1];
}

/** The last n month keys ending at `endKey`, oldest first. */
export function monthKeys(n: number, endKey: string): string[] {
  const keys: string[] = [];
  const iso = endKey + '-01';
  for (let i = n - 1; i >= 0; i--) keys.push(monthKey(addMonths(iso, -i)));
  return keys;
}

export interface DateOptions {
  /** Adds the year: "26 May 2026". */
  year?: boolean;
  /** Starts with the weekday: "Tue 26 May". */
  weekday?: boolean;
}

/** "26 May", "Tue 26 May", "26 May 2026"; empty for no date. */
export function fmtDate(iso: string | null | undefined, o: DateOptions = {}): string {
  if (!iso) return '';
  const p = String(iso).slice(0, 10).split('-');
  const y = +p[0];
  const m = +p[1];
  const d = +p[2];
  let s = d + ' ' + MONTHS[m - 1];
  if (o.weekday) s = DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] + ' ' + s;
  if (o.year) s += ' ' + y;
  return s;
}

/** A date in a sentence: the year only when it is not `today`'s year. */
export function fmtDateNear(iso: string | null | undefined, today: string): string {
  if (!iso) return '';
  return fmtDate(iso, { year: iso.slice(0, 4) !== today.slice(0, 4) });
}

// ── Words ────────────────────────────────────────────────────────────────────────────────────────

export function daysWord(n: number): string {
  return n === 1 ? '1 day' : n + ' days';
}

/** "Overdue 3 days", "Due today", "Due tomorrow", "Due in 5 days" (days = due date − today). */
export function dueWord(days: number): string {
  if (days < 0) return 'Overdue ' + daysWord(-days);
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return 'Due in ' + daysWord(days);
}

/** "1 invoice", "3 invoices". */
export function plural(n: number, one: string, many: string): string {
  return n + ' ' + (n === 1 ? one : many);
}

/** "a", "a and b", "a, b and c". */
export function joinAnd(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
}

/** Up to two initials: "GM" for GM Voices. */
export function initialsOf(name: string): string {
  return String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}
